import { useMemo } from 'react';
import type { EntityType } from '@einhaus/project314-types';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner';
import type { EditorConditionNode, EditorGroupNode, ScreenerFeatureDefinition, ScreenerGroupOperator } from '../types/screenerEditor';
import RuleGroup from './RuleGroup';
import {
    createEditorCondition,
    createEditorGroup,
    removeNodeAtPath,
    updateGroupAtPath,
    updateNodeAtPath
} from '../utils/screenerEditorUtils';

interface RuleBuilderProps {
    rootGroup: EditorGroupNode;
    features: readonly ScreenerFeatureDefinition[];
    featuresLoading: boolean;
    entityTypes: readonly EntityType[];
    onChange: (rootGroup: EditorGroupNode) => void;
}

const RuleBuilder = ({ rootGroup, features, featuresLoading, entityTypes, onChange }: RuleBuilderProps) => {
    const featureOptions = useMemo(
        () =>
            [...features]
                .filter((feature) => entityTypes.some((entityType) => feature.supportedEntityTypes.includes(entityType)))
                .sort((a, b) => a.label.localeCompare(b.label) || a.key.localeCompare(b.key))
                .map((f) => ({
                    value: f.key,
                    label: entityTypes.every((entityType) => f.supportedEntityTypes.includes(entityType))
                        ? f.label
                        : `${f.label} (${entityTypes.filter((entityType) => f.supportedEntityTypes.includes(entityType)).join(', ')} only)`
                })),
        [features, entityTypes]
    );

    const handleUpdateCondition = (path: number[], updater: (node: EditorConditionNode) => EditorConditionNode) => {
        onChange(
            updateNodeAtPath(rootGroup, path, (n) => {
                if (n.type !== 'condition') return n;
                return updater(n);
            })
        );
    };

    const handleSetGroupOperator = (path: number[], operator: ScreenerGroupOperator) => {
        onChange(updateGroupAtPath(rootGroup, path, (g) => ({ ...g, operator })));
    };

    const handleAddCondition = (groupPath: number[]) => {
        onChange(
            updateGroupAtPath(rootGroup, groupPath, (g) => ({
                ...g,
                children: [...g.children, createEditorCondition()]
            }))
        );
    };

    const handleAddGroup = (groupPath: number[]) => {
        onChange(
            updateGroupAtPath(rootGroup, groupPath, (g) => ({
                ...g,
                children: [...g.children, createEditorGroup('and', [createEditorCondition()])]
            }))
        );
    };

    const handleRemoveNode = (path: number[]) => {
        onChange(removeNodeAtPath(rootGroup, path));
    };

    if (featuresLoading) {
        return <DataLoadingSpinner />;
    }

    return (
        <div className="py-2">
            <RuleGroup
                depth={0}
                featureOptions={featureOptions}
                features={features}
                node={rootGroup}
                path={[]}
                onAddCondition={handleAddCondition}
                onAddGroup={handleAddGroup}
                onRemoveNode={handleRemoveNode}
                onSetGroupOperator={handleSetGroupOperator}
                onUpdateCondition={handleUpdateCondition}
            />
        </div>
    );
};

export default RuleBuilder;
